import { Button } from "@/components/ui/button";
import { Link } from "@inertiajs/react";
import {
  Boxes,
  Globe,
  Puzzle,
  Receipt,
  RefreshCw,
  Tag,
  ToyBrick,
} from "lucide-react";
import heroImage from "/assets/hero.png";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { privacy_path, refund_path, terms_path } from "@/lib/routes";
import useTranslations from "@/hooks/use-translations";
import { UserNavigation } from "@/components/application/header-navigation";
import LanguageSwitcher from "@/components/application/language-switcher";

export default function Landing() {
  const { t, raw } = useTranslations("site.landing");

  const features = raw<{ title: string; description: string }[]>(
    "sections.features.features"
  )!;

  const plans = raw<{ title: string; summary: string; description: string }[]>(
    "sections.pricing.plans"
  )!;

  return (
    <div className="min-h-screen">
      <header className="bg-secondary flex w-full items-center justify-center px-8 py-4.5">
        <div className="flex w-full justify-between gap-16">
          <div className="flex items-center gap-5">
            <Link className="flex items-center gap-2 font-bold">
              <ToyBrick className="size-8" /> BrickGestor
            </Link>
          </div>
          <ul className="flex flex-1 items-center gap-6">
            <li>
              <Link href={terms_path()}>{t("site.footer.terms")}</Link>
            </li>
            <li>
              <Link href={privacy_path()}>{t("site.footer.privacy")}</Link>
            </li>
            <li>
              <Link href={refund_path()}>{t("site.footer.refund")}</Link>
            </li>
          </ul>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <UserNavigation />
          </div>
        </div>
      </header>

      <section className="bg-secondary py-16 md:py-24">
        <div className="px-4 md:px-8 mx-auto w-full">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center gap-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold">
              {t("title")}
            </h1>
            <p className="text-xl max-w-3xl">{t("tagline")}</p>
            <img src={heroImage} />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-4 md:px-8 mx-auto w-full">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center gap-6">
            <h2 className="text-3xl md:text-4xl font-semibold text-center">
              {t("sections.features.title")}
            </h2>
            <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="py-8 flex flex-col gap-4">
                <div className="p-3 shadow-xs rounded-lg flex mx-auto border border-border">
                  <Puzzle className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">{features[0].title}</h3>
                <p>{features[0].description}</p>
              </div>
              <div className="py-8 flex flex-col gap-4">
                <div className="p-3 shadow-xs rounded-lg flex mx-auto border border-border">
                  <Boxes className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">{features[1].title}</h3>
                <p>{features[1].description}</p>
              </div>
              <div className="py-8 flex flex-col gap-4">
                <div className="p-3 shadow-xs rounded-lg flex mx-auto border border-border">
                  <Tag className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">{features[2].title}</h3>
                <p>{features[2].description}</p>
              </div>
              <div className="py-8 flex flex-col gap-4">
                <div className="p-3 shadow-xs rounded-lg flex mx-auto border border-border">
                  <Globe className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">{features[3].title}</h3>
                <p>{features[3].description}</p>
              </div>
              <div className="py-8 flex flex-col gap-4">
                <div className="p-3 shadow-xs rounded-lg flex mx-auto border border-border">
                  <Receipt className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">{features[4].title}</h3>
                <p>{features[4].description}</p>
              </div>
              <div className="py-8 flex flex-col gap-4">
                <div className="p-3 shadow-xs rounded-lg flex mx-auto border border-border">
                  <RefreshCw className="size-6" />
                </div>
                <h3 className="text-lg font-semibold">{features[5].title}</h3>
                <p>{features[5].description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border border-t-border">
        <div className="px-4 md:px-8 mx-auto w-full">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center gap-6">
            <h2 className="text-3xl md:text-4xl font-semibold text-center">
              {t("sections.pricing.title")}
            </h2>
            <div className="flex gap-8 mt-16 flex-col md:flex-row">
              <Card className="max-w-96 py-8">
                <CardHeader className="px-8 text-left">
                  <h3 className="text-5xl md:text-6xl font-semibold text-left">
                    {plans[0].title}
                  </h3>
                  <p className="text-gray-500">{plans[0].summary}</p>
                  <Button asChild size="lg" className="mt-6">
                    <Link href="/sign_up">
                      {t("sections.pricing.cta")}
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent className="px-8 text-left">
                  <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
                    {plans[0].description
                      .trim()
                      .split("\n")
                      .map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="max-w-96 py-8">
                <CardHeader className="px-8 text-left">
                  <h3 className="flex gap-1 items-baseline">
                    <div className="text-5xl md:text-6xl font-semibold">
                      US$ 10
                    </div>
                    <div className="text-base font-medium">
                      {plans[1].title}
                    </div>
                  </h3>
                  <p className="text-gray-500">{plans[1].summary}</p>
                  <Button asChild size="lg" className="mt-6">
                    <Link href="/sign_up?plan=basic">
                      {t("sections.pricing.cta")}
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent className="px-8 text-left">
                  <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
                    {plans[1].description
                      .trim()
                      .split("\n")
                      .map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid gap-8 text-center md:text-left md:grid-cols-4">
            <div>
              <Link className="flex items-center gap-2 font-bold">
                <ToyBrick className="size-8" /> BrickGestor
              </Link>
              <p className="mt-2 text-xs opacity-75">
                {t("site.footer.made_with")}{" "}
                <a href="https://gatitopirata.com">
                  {t("company_name")}
                </a>
              </p>
              <p className="mt-2 text-xs opacity-75">
                {t("trademark_disclaimer")}
              </p>
            </div>
            {/* <nav>
              <p className="font-semibold">{t("site.footer.product")}</p>
              <ul className="mt-2 space-y-1">
                <li>
                  <a href="/#features">{t("site.footer.features")}</a>
                </li>
                <li>
                  <a href="/pricing">{t("site.footer.pricing")}</a>
                </li>
                <li>
                  <a href="/#faq">{t("site.footer.faq")}</a>
                </li>
                <li>
                  <a href="/blog">{t("site.footer.blog")}</a>
                </li>
              </ul>
            </nav> */}
            <nav>
              <p className="font-semibold">{t("site.footer.account")}</p>
              <ul className="mt-2 space-y-1">
                <li>
                  <Link href="/sign_up">{t("site.footer.sign_up")}</Link>
                </li>
                <li>
                  <Link href="/sign_in">{t("site.footer.sign_in")}</Link>
                </li>
              </ul>
            </nav>
            <nav>
              <p className="font-semibold">{t("site.footer.about")}</p>
              <ul className="mt-2 space-y-1">
                <li>
                  <a href="/privacy">{t("site.footer.privacy")}</a>
                </li>
                <li>
                  <a href="/terms">{t("site.footer.terms")}</a>
                </li>
                <li>
                  <a href="/refund">{t("site.footer.refund")}</a>
                </li>
                <li>
                  <a href="/complaints/new">
                    {t("site.footer.complaint_book")}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
